import { APIProvider, AdvancedMarker, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import React from 'react';
import { Image, Pressable, Text as RNText, View } from 'react-native';
import Supercluster from 'supercluster';

import { imgMockAcademy } from '@/assets/images';
import { SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling, type Coords } from '@/hooks';
import { envPublic } from '@/libs/config/env.public';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

import type { NearbyCoach } from '../onboarding-step-find-academy';
import {
  CLUSTER_MAX_ZOOM,
  CLUSTER_RADIUS,
  DEFAULT_REGION,
  FOCUS_SPAN_MILES,
  INITIAL_SPAN_MILES,
  regionForSpanMiles,
  regionToZoom,
} from './geo';
import { MapSearchBar } from './map-search-bar';
import { PinFrame, pinMetrics } from './pin-frame';

/** Per-point properties carried through Supercluster (mirrors the native map). */
type AcademyClusterProps = { academyId: string };

/**
 * Google-supplied Map ID that enables Advanced Markers on a vector map without any
 * Cloud console setup. Swap for a real Cloud Map ID if we ever want to apply a custom
 * (e.g. desaturated) base style on web — the "disabled" grey state is done with a CSS
 * grayscale filter here instead, so it works on the DEMO map too.
 */
const DEMO_MAP_ID = 'DEMO_MAP_ID';

type OnboardingAcademyMapProps = {
  academies: NearbyCoach[];
  onSelectAcademy?: (id: string) => void;
  onSearch?: (text: string) => void;
  userLocation?: Coords | null;
  /** The guardian's real GPS position — distinct from `userLocation`, which the parent sets to
   *  the search centre while a zip is active. Drives the "my location" recenter button (the web
   *  stand-in for `showsMyLocationButton`, which the Maps JS API has no built-in equivalent for). */
  myLocation?: Coords | null;
  /** Re-request the device location on demand. A button tap is a valid user gesture for the web
   *  geolocation prompt, so we ride it to (re-)fetch when we don't yet have a fix. */
  onRequestLocation?: () => Promise<Coords | null>;
  /** The geocoded address / zip-code the guardian searched — draws a blue dot at that point. */
  searchedLocation?: Coords | null;
  /** Unused on web (region → Nearest-list filtering is native-only), kept for prop parity. */
  onRegionChange?: (region: {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
  }) => void;
  disabled?: boolean;
  /** True while the parent is geocoding a search query — drives the search bar's spinner. */
  searching?: boolean;
  /** When set (and on each change), the camera pans/zooms to this coach's venue — driven by a
   *  marker tap or a Nearest-list selection in the parent. */
  focusCoordinate?: Coords | null;
  /** Currently-selected coach id. Its marker is lifted above others sharing the same
   *  coordinate (some venues have identical lat/long). */
  selectedId?: string | null;
};

/**
 * Web implementation of {@link OnboardingAcademyMap}. `react-native-maps` is native-only,
 * so on web we drive the Maps JavaScript API through `@vis.gl/react-google-maps`. Markers
 * cluster with the same Supercluster tuning as native and reuse the shared `PinFrame`, so
 * the pins look identical across platforms.
 *
 * The API key is the native `GOOGLE_MAP_API_KEY`, surfaced to the web bundle via
 * `expo.extra`. Without a key we fall back to the informational panel so onboarding still
 * works (e.g. in a preview build that never configured Maps).
 */
function OnboardingAcademyMapComponent({
  academies,
  onSelectAcademy,
  onSearch,
  userLocation,
  myLocation,
  onRequestLocation,
  searchedLocation,
  disabled,
  searching,
  focusCoordinate,
  selectedId,
}: OnboardingAcademyMapProps) {
  const styles = useStyles();
  const apiKey = envPublic.googleMapsApiKey;

  if (!apiKey) return <MapUnavailable academies={academies} />;

  return (
    <View style={styles.mapWrapper}>
      <APIProvider apiKey={apiKey}>
        <GoogleMap
          mapId={DEMO_MAP_ID}
          defaultCenter={{ lat: DEFAULT_REGION.latitude, lng: DEFAULT_REGION.longitude }}
          defaultZoom={regionToZoom(DEFAULT_REGION)}
          gestureHandling="greedy"
          disableDefaultUI
          clickableIcons={false}
          // No inline base-map styling (that needs a raster map, incompatible with the
          // Advanced Markers we render): grey the whole thing out with a CSS filter instead.
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            width: '100%',
            height: '100%',
            filter: disabled ? 'grayscale(1)' : undefined,
          }}>
          <MapLayer
            academies={academies}
            onSelectAcademy={onSelectAcademy}
            userLocation={userLocation}
            searchedLocation={searchedLocation}
            disabled={disabled}
            focusCoordinate={focusCoordinate}
            selectedId={selectedId}
          />
        </GoogleMap>

        {/* "My location" recenter button — web has no built-in one. Sibling of <Map> (still
            inside <APIProvider>) so useMap() resolves the default map instance. Hidden while
            the map is disabled (no location to show). */}
        {!disabled && myLocation ? (
          <MyLocationButton target={myLocation} onRequestLocation={onRequestLocation} />
        ) : null}
      </APIProvider>

      {/* Floating address / zip-code search — same component as native. */}
      <MapSearchBar onSearch={onSearch} loading={searching} />
    </View>
  );
}

/**
 * Lives inside <Map> so it can read the map instance. Owns the clustering + camera work:
 * indexes the academies, recentres on the resolved location, recomputes visible clusters
 * on every pan/zoom, and renders a marker per cluster / academy.
 */
function MapLayer({
  academies,
  onSelectAcademy,
  userLocation,
  searchedLocation,
  disabled,
  focusCoordinate,
  selectedId,
}: Pick<
  OnboardingAcademyMapProps,
  | 'academies'
  | 'onSelectAcademy'
  | 'userLocation'
  | 'searchedLocation'
  | 'disabled'
  | 'focusCoordinate'
  | 'selectedId'
>) {
  const map = useMap();

  // West/South/East/North of the visible viewport + integer zoom, refreshed whenever the
  // camera settles ("idle"). Null bbox until the first idle, so markers appear once the
  // map has laid out.
  const [bbox, setBbox] = React.useState<GeoJSON.BBox | null>(null);
  const [zoom, setZoom] = React.useState(regionToZoom(DEFAULT_REGION));

  // Index the academies once per data change; a lookup map keeps the full record around so
  // a leaf can render its thumbnail without threading it through cluster properties.
  const index = React.useMemo(() => {
    const supercluster = new Supercluster<AcademyClusterProps>({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
    });
    supercluster.load(
      academies.map(academy => ({
        type: 'Feature' as const,
        properties: { academyId: academy.id },
        geometry: {
          type: 'Point' as const,
          coordinates: [academy.coordinate.longitude, academy.coordinate.latitude],
        },
      })),
    );
    return supercluster;
  }, [academies]);

  const academyById = React.useMemo(
    () => new Map<string, NearbyCoach>(academies.map(academy => [academy.id, academy])),
    [academies],
  );

  // Recentre on the guardian's resolved position, keeping the ~100-mile opening span. No
  // move when it's null — the camera stays on DEFAULT_REGION behind the greyed-out map.
  React.useEffect(() => {
    if (!map || !userLocation) return;
    const region = regionForSpanMiles(userLocation, INITIAL_SPAN_MILES);
    map.panTo({ lat: userLocation.latitude, lng: userLocation.longitude });
    map.setZoom(regionToZoom(region));
  }, [map, userLocation]);

  // Pan/zoom to a coach picked from a marker click or the Nearest list. Runs on every change
  // (the parent hands us a fresh object per selection) so re-picking the same coach recentres.
  React.useEffect(() => {
    if (!map || !focusCoordinate) return;
    map.panTo({ lat: focusCoordinate.latitude, lng: focusCoordinate.longitude });
    map.setZoom(regionToZoom(regionForSpanMiles(focusCoordinate, FOCUS_SPAN_MILES)));
  }, [map, focusCoordinate]);

  // Keep bbox + zoom in step with the camera so getClusters recomputes for what's on screen.
  React.useEffect(() => {
    if (!map) return;
    const sync = () => {
      const bounds = map.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        setBbox([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
      }
      const z = map.getZoom();
      if (z != null) setZoom(Math.round(z));
    };
    sync();
    const listener = map.addListener('idle', sync);
    return () => listener.remove();
  }, [map]);

  const clusters = React.useMemo(
    () => (bbox ? index.getClusters(bbox, zoom) : []),
    [index, bbox, zoom],
  );

  // Tapping a cluster zooms to the level Supercluster says splits it, centred on the cluster.
  const handleClusterPress = React.useCallback(
    (clusterId: number, lng: number, lat: number) => {
      if (!map) return;
      const expansionZoom = Math.min(index.getClusterExpansionZoom(clusterId), 20);
      map.panTo({ lat, lng });
      map.setZoom(expansionZoom);
    },
    [map, index],
  );

  return (
    <>
      {!disabled && userLocation ? (
        <AdvancedMarker
          position={{ lat: userLocation.latitude, lng: userLocation.longitude }}
          // Centre the dot on the point (native uses anchor {0.5,0.5}); AdvancedMarker
          // defaults to bottom-centre, which would float the dot above the real location.
          anchorLeft="-50%"
          anchorTop="-50%"
          zIndex={1}>
          <UserDot />
        </AdvancedMarker>
      ) : null}

      {/* Blue dot at the searched address / zip-code. Above pins so it's never hidden. */}
      {searchedLocation ? (
        <AdvancedMarker
          position={{ lat: searchedLocation.latitude, lng: searchedLocation.longitude }}
          // Centre-anchor so the dot sits on the geocoded point, matching the native marker.
          anchorLeft="-50%"
          anchorTop="-50%"
          zIndex={1000}>
          <UserDot />
        </AdvancedMarker>
      ) : null}

      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates;

        if ('cluster' in feature.properties) {
          const clusterId = feature.id as number;
          return (
            <AdvancedMarker
              key={`cluster-${clusterId}`}
              position={{ lat, lng }}
              onClick={() => handleClusterPress(clusterId, lng, lat)}>
              <PinFrame isCluster>
                <ClusterCount count={feature.properties.point_count} />
              </PinFrame>
            </AdvancedMarker>
          );
        }

        const academy = academyById.get(feature.properties.academyId);
        if (!academy) return null;
        return (
          <AdvancedMarker
            key={academy.id}
            position={{ lat, lng }}
            // Selected pin floats above others stacked on the exact same coordinate.
            zIndex={academy.id === selectedId ? 999 : undefined}
            onClick={() => onSelectAcademy?.(academy.id)}>
            <AcademyThumb academy={academy} />
          </AdvancedMarker>
        );
      })}
    </>
  );
}

/** The academy thumbnail slotted into the shared teardrop pin (matches the native marker). */
function AcademyThumb({ academy }: { academy: NearbyCoach }) {
  const { scale } = useScaling();
  const { headSize } = pinMetrics(scale);
  const imageSize = headSize - scale(1) * 2;
  return (
    <PinFrame>
      <Image
        source={academy.thumb ?? imgMockAcademy}
        resizeMode="cover"
        style={{ width: imageSize, height: imageSize, borderRadius: imageSize / 2 }}
      />
    </PinFrame>
  );
}

/** The number shown inside a cluster pin's head. */
function ClusterCount({ count }: { count: number }) {
  const styles = useStyles();
  return (
    <RNText style={styles.clusterCount} numberOfLines={1}>
      {count}
    </RNText>
  );
}

/**
 * Web stand-in for `react-native-maps`' `showsMyLocationButton`. Pans/zooms the camera onto the
 * guardian's GPS position. When we don't have a fix yet, the tap itself is the user gesture the
 * browser geolocation prompt needs — so we (re-)request, then centre on whatever it resolves.
 */
function MyLocationButton({
  target,
  onRequestLocation,
}: {
  target?: Coords | null;
  onRequestLocation?: () => Promise<Coords | null>;
}) {
  const map = useMap();
  const styles = useStyles();

  const handlePress = React.useCallback(async () => {
    const coords = target ?? (await onRequestLocation?.());
    if (!map || !coords) return;
    map.panTo({ lat: coords.latitude, lng: coords.longitude });
    map.setZoom(regionToZoom(regionForSpanMiles(coords, FOCUS_SPAN_MILES)));
  }, [map, target, onRequestLocation]);

  return (
    <Pressable style={styles.myLocationButton} onPress={handlePress} accessibilityRole="button">
      <SvgIcon name="IcMyLocation" size={20} color={guardianColors['Main/Main']} />
    </Pressable>
  );
}

/** Blue "you are here" dot, since web has no native blue-dot layer. */
function UserDot() {
  const styles = useStyles();
  return (
    <View style={styles.userDotHalo}>
      <View style={styles.userDot} />
    </View>
  );
}

/** Shown when no Maps key is configured: the previous informational panel, so onboarding
 *  still renders instead of a blank / broken map. */
function MapUnavailable({ academies }: { academies: NearbyCoach[] }) {
  const styles = useStyles();
  const count = academies.length;
  return (
    <View style={[styles.mapWrapper, styles.unavailable]}>
      <SvgIcon name="IcPin" width={40} height={54} />
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.unavailableTitle}>
        {count} {count === 1 ? 'academy' : 'academies'} nearby
      </Text>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
        style={styles.unavailableSubtitle}>
        Map preview is unavailable.
      </Text>
    </View>
  );
}

export const OnboardingAcademyMap = React.memo(OnboardingAcademyMapComponent);

const useStyles = createStyles(({ scale }) => ({
  mapWrapper: {
    marginTop: scale(16),
    height: scale(300),
    borderRadius: scale(12),
    overflow: 'hidden',
    backgroundColor: colors['1'],
  },
  clusterCount: {
    color: colors['0'],
    fontWeight: '700',
    fontSize: scale(13),
  },
  userDotHalo: {
    width: scale(22),
    height: scale(22),
    borderRadius: scale(11),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withOpacity(colors.Blue, 0.2),
  },
  userDot: {
    width: scale(14),
    height: scale(14),
    borderRadius: scale(7),
    borderWidth: scale(2),
    borderColor: colors['0'],
    backgroundColor: colors.Blue,
  },
  myLocationButton: {
    position: 'absolute',
    right: scale(12),
    bottom: scale(12),
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['0'],
    // Float above the map tiles/markers and lift off the surface a touch.
    zIndex: 1,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: scale(4),
    shadowOffset: { width: 0, height: scale(2) },
  },
  unavailable: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
  },
  unavailableTitle: {
    color: guardianColors['Main/Main'],
  },
  unavailableSubtitle: {
    color: withOpacity(guardianColors['Main/Main'], 0.6),
  },
}));

export default OnboardingAcademyMap;
